import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { request } from "../../config/helpers/axios-instance";
import GetGLP1Orders from "../../hooks/fetch-hooks/get-glp1-orders/get-glp1-orders";
import StatCard from "./components/StatCard";
import StatusBadge from "./components/StatusBadge";
import TATBar from "./components/TATBar";
import TATDistribution from "./components/TATDistribution";
import { MdRefresh, MdChevronLeft, MdChevronRight } from "react-icons/md";

const PAGE_SIZE = 20;

// Same grouping the TAT dashboard uses for its "In Transit" chip
// (pillup-api classes/Tat/Constants.js SHIPPED_STATUSES) — kept in sync so "in transit"
// means the same thing on both screens.
const IN_TRANSIT_STATUSES = ["SHIPPED_IN_TRANSIT", "SHIPPED_VIA_PILLUP", "SHIPPED_VIA_ZYDUS"];
const DELIVERED_STATUSES = ["DELIVERED", "ORDER_DELIVERED"];

const useQuery = () => new URLSearchParams(useLocation().search);

function fmtShort(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit", month: "short",
        hour: "2-digit", minute: "2-digit", hour12: true,
    });
}

function tatAvgLabel(min) {
    if (min === null) return "—";
    const d = Math.floor(min / 1440);
    const h = Math.floor((min % 1440) / 60);
    const m = min % 60;
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

const COL_HEADERS = [
    { label: "Patient",        sortable: false },
    { label: "Order ID",       sortable: false },
    { label: "Status",         sortable: false },
    { label: "Address",        sortable: false },
    { label: "Confirmed At",   sortable: true,  key: "confirmedAt" },
    { label: "Packed At",      sortable: false },
    { label: "TAT",            sortable: true,  key: "tatMinutes"  },
    { label: "Delivery Init.", sortable: false },
    { label: "Delivered At",   sortable: false },
    { label: "Action",         sortable: false },
];

function GLP1Dashboard() {
    const query = useQuery();
    const [, setSearchParams] = useSearchParams();

    const [search, setSearch]           = useState(query.get("search") || "");
    const [statusFilter, setStatusFilter] = useState(query.get("status") || "all");
    const [sortCol, setSortCol]         = useState("confirmedAt");
    const [sortDir, setSortDir]         = useState("desc");
    const [pageNum, setPageNum]         = useState(Number(query.get("page")) || 1);

    const { data, isLoading, isError, refetch, isFetching } = GetGLP1Orders(pageNum, PAGE_SIZE);

    const [markingDeliveryId, setMarkingDeliveryId] = useState(null);

    async function markAsDelivered(deliveryId) {
        if (!deliveryId || markingDeliveryId) return;
        setMarkingDeliveryId(deliveryId);
        try {
            const response = await request({
                method: "POST",
                url: `delivery/markDelivered/${deliveryId}`,
            });
            if (response.data?.success) {
                toast.success("Order marked delivered");
                refetch();
            } else {
                toast.error(response.data?.err || "Error marking delivered");
            }
        } catch (err) {
            toast.error("Error marking delivered");
        } finally {
            setMarkingDeliveryId(null);
        }
    }

    const orders = useMemo(() => data?.data?.data?.rows ?? [], [data]);
    const totalCount = data?.data?.data?.count ?? 0;
    const totalPages = Math.max(Math.ceil(totalCount / PAGE_SIZE), 1);

    const allStatuses = useMemo(
        () => Array.from(new Set(orders.map(o => o.status?.toUpperCase()).filter(Boolean))),
        [orders]
    );

    const filtered = useMemo(() => {
        let out = orders.filter(o => {
            if (statusFilter !== "all" && o.status?.toUpperCase() !== statusFilter) return false;
            if (!search) return true;
            const q = search.toLowerCase();
            return (
                (o.patientName ?? "").toLowerCase().includes(q) ||
                String(o.orderId).includes(q) ||
                (o.patientPhone ?? "").includes(q) ||
                (o.address ?? "").toLowerCase().includes(q)
            );
        });
        return [...out].sort((a, b) => {
            const aInTransit = IN_TRANSIT_STATUSES.includes(a.status?.toUpperCase());
            const bInTransit = IN_TRANSIT_STATUSES.includes(b.status?.toUpperCase());
            if (aInTransit !== bInTransit) return aInTransit ? -1 : 1;

            let av, bv;
            if (sortCol === "tatMinutes") {
                av = a.tatMinutes ?? -1;
                bv = b.tatMinutes ?? -1;
            } else {
                av = a.confirmedAt ? new Date(a.confirmedAt).getTime() : 0;
                bv = b.confirmedAt ? new Date(b.confirmedAt).getTime() : 0;
            }
            return sortDir === "asc" ? av - bv : bv - av;
        });
    }, [orders, search, statusFilter, sortCol, sortDir]);

    const maxTAT = useMemo(() => Math.max(...filtered.map(o => o.tatMinutes ?? 0), 1), [filtered]);

    const stats = data?.data?.data?.stats;
    const avgTAT = stats?.avgTatMinutes ?? null;
    const packedCount = stats?.packedCount ?? 0;
    const deliveredCount = stats?.deliveredCount ?? 0;
    const tatDistribution = stats?.tatDistribution ?? null;

    const prevFiltersRef = useRef({ search, statusFilter });

    useEffect(() => {
        const prev = prevFiltersRef.current;
        const filtersChanged = prev.search !== search || prev.statusFilter !== statusFilter;
        prevFiltersRef.current = { search, statusFilter };

        const effectivePage = filtersChanged ? 1 : pageNum;
        if (filtersChanged && pageNum !== 1) {
            setPageNum(1);
            return; // the pageNum update below will re-run this effect with effectivePage already applied
        }

        setSearchParams(
            { page: effectivePage, size: PAGE_SIZE, search, status: statusFilter },
            { replace: true }
        );
    }, [pageNum, search, statusFilter, setSearchParams]);

    function toggleSort(col) {
        if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortCol(col); setSortDir("desc"); }
    }

    return (
        <div className="p-7 h-full overflow-y-scroll bg-gray-50">

            {/* Page header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 font-sans">GLP-1 Orders</h1>
                    <p className="text-sm text-gray-400 mt-0.5">source = GLP_1 &middot; channel = DOCTOR</p>
                </div>
                <button
                    onClick={refetch}
                    disabled={isFetching}
                    className="flex items-center gap-2 bg-[#28A48B] hover:bg-[#229076] text-white-50 text-sm font-semibold px-4 py-2 rounded-2xl disabled:opacity-50 transition"
                >
                    <MdRefresh className={`text-base ${isFetching ? "animate-spin" : ""}`} />
                    {isFetching ? "Refreshing…" : "Refresh"}
                </button>
            </div>

            {/* Stat cards */}
            <div className="flex gap-4 mb-6">
                <StatCard
                    label="Total Orders"
                    value={totalCount}
                    sub="across all pages"
                />
                <StatCard
                    label="Packed"
                    value={packedCount}
                    sub="awaiting dispatch"
                />
                <StatCard
                    label="Delivered"
                    value={deliveredCount}
                    sub={`${Math.round(deliveredCount / Math.max(totalCount, 1) * 100)}% of total`}
                />
                <StatCard
                    label="Avg TAT"
                    value={tatAvgLabel(avgTAT)}
                    sub="order confirmed → delivery"
                />
            </div>

            {/* TAT distribution */}
            <div className="mb-6">
                <TATDistribution distribution={tatDistribution} />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 min-w-[240px] max-w-sm">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search name, order ID, phone, address…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-[#28A48B] focus:ring-1 focus:ring-[#28A48B]"
                    />
                </div>

                <div className="flex gap-2 flex-wrap">
                    {["all", ...allStatuses].map(s => (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(s)}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                                statusFilter === s
                                    ? "bg-[#28A48B] text-white-50 border-[#28A48B]"
                                    : "bg-white text-gray-500 border-gray-200 hover:border-[#28A48B] hover:text-[#28A48B]"
                            }`}
                        >
                            {s === "all" ? "All" : s.replace("ORDER_", "").replace(/_/g, " ")}
                        </button>
                    ))}
                </div>

                <span className="ml-auto text-xs text-gray-400 font-sans">
                    {filtered.length} order{filtered.length !== 1 ? "s" : ""}
                </span>
            </div>

            {/* Error */}
            {isError && (
                <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-3 mb-4 text-sm">
                    Failed to load orders. Check API connection.
                </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full font-sans">
                        <thead>
                            <tr className="text-left h-14 border-b border-gray-100">
                                {COL_HEADERS.map(h => (
                                    <th
                                        key={h.label}
                                        onClick={h.sortable ? () => toggleSort(h.key) : undefined}
                                        className={`px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap select-none ${h.sortable ? "cursor-pointer hover:text-[#28A48B]" : ""} ${sortCol === h.key ? "text-[#28A48B]" : ""}`}
                                    >
                                        {h.label}
                                        {h.sortable && (
                                            <span className="ml-1 text-[10px]">
                                                {sortCol === h.key ? (sortDir === "desc" ? "↓" : "↑") : "↕"}
                                            </span>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i} className="border-b border-gray-50">
                                        {Array.from({ length: 10 }).map((_, j) => (
                                            <td key={j} className="px-4 py-4">
                                                <div className="h-3 rounded bg-gray-100 animate-pulse" style={{ width: j === 2 ? "60%" : "80%" }} />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="px-4 py-16 text-center text-gray-400 text-sm">
                                        No orders found.
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((o, i) => (
                                    <tr
                                        key={o.orderId}
                                        className={`border-b border-gray-50 hover:bg-slate-100 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
                                    >
                                        {/* Patient */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <p className="text-sm font-semibold text-gray-800">{o.patientName ?? <span className="text-gray-300">—</span>}</p>
                                            <p className="text-xs text-gray-400 font-mono mt-0.5">{o.patientPhone ?? ""}</p>
                                        </td>
                                        {/* Order ID */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-1 rounded-lg">
                                                #{o.orderId}
                                            </span>
                                        </td>
                                        {/* Status */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <StatusBadge status={o.status} />
                                        </td>
                                        {/* Address */}
                                        <td className="px-4 py-3 max-w-[200px]">
                                            <span className="text-xs text-gray-500 block truncate" title={o.address}>
                                                {o.address || "—"}
                                            </span>
                                        </td>
                                        {/* Confirmed */}
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 font-mono">
                                            {fmtShort(o.confirmedAt)}
                                        </td>
                                        {/* Packed */}
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 font-mono">
                                            {fmtShort(o.packedAt)}
                                        </td>
                                        {/* TAT */}
                                        <td className="px-4 py-3 min-w-[140px]">
                                            <TATBar minutes={o.tatMinutes} max={maxTAT} pending={o.tatPending} />
                                        </td>
                                        {/* Delivery */}
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 font-mono">
                                            {fmtShort(o.deliveryInitiatedAt)}
                                        </td>
                                        {/* Delivered At */}
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 font-mono">
                                            {fmtShort(o.deliveredAt)}
                                        </td>
                                        {/* Action */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {!DELIVERED_STATUSES.includes(o.status?.toUpperCase()) && o.deliveryId && (
                                                <button
                                                    onClick={() => markAsDelivered(o.deliveryId)}
                                                    disabled={markingDeliveryId === o.deliveryId}
                                                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-[#28A48B] text-[#28A48B] hover:bg-[#28A48B] hover:text-white-50 disabled:opacity-50 transition"
                                                >
                                                    {markingDeliveryId === o.deliveryId ? "Marking…" : "Mark as Delivered"}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
                    <span>source=GLP_1 · channel=DOCTOR</span>
                    <span>auto-refresh every 60s</span>
                </div>

                {/* Pagination */}
                {totalCount > 0 && (
                    <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-xs text-gray-400">
                            Showing {(pageNum - 1) * PAGE_SIZE + 1}–{Math.min(pageNum * PAGE_SIZE, totalCount)} of {totalCount}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setPageNum(p => Math.max(p - 1, 1))}
                                disabled={pageNum === 1}
                                className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 text-gray-500 hover:border-[#28A48B] hover:text-[#28A48B] disabled:opacity-40 disabled:hover:border-gray-200 disabled:hover:text-gray-500 transition"
                            >
                                <MdChevronLeft className="text-lg" />
                            </button>
                            <span className="text-xs font-semibold text-gray-600 px-3 font-mono">
                                {pageNum} / {totalPages}
                            </span>
                            <button
                                onClick={() => setPageNum(p => Math.min(p + 1, totalPages))}
                                disabled={pageNum === totalPages}
                                className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 text-gray-500 hover:border-[#28A48B] hover:text-[#28A48B] disabled:opacity-40 disabled:hover:border-gray-200 disabled:hover:text-gray-500 transition"
                            >
                                <MdChevronRight className="text-lg" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default GLP1Dashboard;
